import { Button, Grid, Popover, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Datatable, { PagingTypeEnum } from '../Datatable/Datatable';
import DatatableColumnModel from '../Datatable/DatatableColumnModel';
import { OrderDirection } from '../Datatable/OrderDirection';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import SurveyorModel from './models/SurveyorModel';
import { userProfileSelector } from '../Login/reducers/UserProfileReducer';
import Swal from 'sweetalert2';
import SurveyorApi, { DeleteSurveyorByIdParam } from './api/SurveyorApi';
import { SurveyorSearchSelector, setCheckedSurveyors, setDatatableSelectedSurveyorIndex, setOrderBy, setOrderDirection, setTriggerSurveyorSearch } from './reducers/SurveyorSearchReducer';
import { SurveyorInputSelector, setSelectedSurveyorId } from './reducers/SurveyorInputReducer';

const SurveyorTable: React.FunctionComponent = () => {
    let dispatch = useDispatch();
    let history = useHistory();

    let SurveyorData = useSelector(SurveyorSearchSelector);
    const SurveyorInputViewData = useSelector(SurveyorInputSelector);
    const userProfile = useSelector(userProfileSelector);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [overviewFullTc, setOverviewFullTc] = useState<string>("");

    const divRef = React.createRef<HTMLDivElement>();

    const columns = [
        {
            Field: Utility.propertyOf<SurveyorModel>("name"),
            Title: "Surveyor Name",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("country"),
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("portNames"),
            Title: "Port Open",
            AllowSort: true,
            Render: (rowData: SurveyorModel) => {
                return <ul>
                    {
                        rowData &&
                        rowData.portNames.map((portName: string) => {
                            return <li>
                                {portName}
                            </li>
                        })
                    }
                </ul>
            }
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("remark"),
            Title: "Remark",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("contactDetailsAndCost"),
            Title: "Contact Details",
            AllowSort: true,
            Render: (rowData: SurveyorModel) => {
                let previewButton = rowData.contactDetailsAndCost && rowData.contactDetailsAndCost.length > 0 ?
                    <i className="fa fa-eye" aria-hidden="true"></i> :
                    <i className="fa fa-eye-slash" aria-hidden="true"></i>;
                return <>
                    <Button
                        className="tonnage-table-preview-full-tc"
                        onMouseEnter={(event: React.MouseEvent<HTMLElement>) => {
                            if (rowData.contactDetailsAndCost && rowData.contactDetailsAndCost.length > 0) {
                                setAnchorEl(event.currentTarget);
                                setOverviewFullTc(rowData.contactDetailsAndCost);
                            }
                        }}
                        onMouseLeave={() => {
                            setAnchorEl(null)
                        }}
                        disabled={!rowData.contactDetailsAndCost || rowData.contactDetailsAndCost.length === 0}
                    >
                        {previewButton}
                    </Button>
                </>
            }
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("userDisplayName"),
            Title: "Broker",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("updatedDate"),
            Title: "Updated Date",
            AllowSort: true,
            Render: (rowData: SurveyorModel) => (
                <>
                    {formatUpdatedDate(rowData.updatedDate).toUpperCase()}
                </>
            )
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: "",
            Title: "",
            AllowSort: true,
            Render: (rowData: SurveyorModel) => (
                <>
                    {userProfile.userGroup === "Administrator" &&
                        <Tooltip title="Delete Surveyor">
                            <Button
                                className='table-action-button'
                                variant='outlined'
                                color='error'
                                onClick={() => {
                                    deleteSurveyor(rowData.id)
                                }}
                            >
                                <i className="fa fa-trash" aria-hidden="true"></i>
                            </Button>
                        </Tooltip>
                    }
                </>
            )
        } as DatatableColumnModel<SurveyorModel>,
    ];

    const columnsForCopy = [
        {
            Field: Utility.propertyOf<SurveyorModel>("name"),
            Title: "Surveyor Name",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("country"),
            Title: "Country",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("portNames"),
            Title: "Port Open",
            AllowSort: true,
            Render: (rowData: SurveyorModel) => {
                return <ul>
                    {
                        rowData &&
                        rowData.portNames.map((portName: string) => {
                            return <li>
                                {portName}
                            </li>
                        })
                    }
                </ul>
            }
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("remark"),
            Title: "Remark",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
        {
            Field: Utility.propertyOf<SurveyorModel>("contactDetailsAndCost"),
            Title: "Contact Details",
            AllowSort: true,
        } as DatatableColumnModel<SurveyorModel>,
    ];

    const deleteSurveyor = (id: string) => {
        Swal.fire({
            title: 'Are you sure want to delete this market info? This action can not be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No'
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));
                SurveyorApi.Delete({
                    id: id
                } as DeleteSurveyorByIdParam)
                    .then((response: BaseResponse<SurveyorModel>) => {
                        if (response.success) {
                            Utility.Alert.Success("Surveyor deleted successful");

                            dispatch(showLoading(false));

                            dispatch(setTriggerSurveyorSearch(new Date().getTime()));
                        }
                    });
            }
        })
    }

    const formatUpdatedDate = (dateString: string): string => {
        if (!dateString) {
            return "";
        }
        let date = new Date(dateString);
        return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const getTableClassName = () => {
        let className = "surveyor-datatable-search-result nice-scroll";

        if (!SurveyorInputViewData.SurveyorInputExpand) {
            className += " surveyor-datatable-search-result-quick-input-collapse-only";
        }

        return className;
    }

    return <div className='datatable-wrapper'>
        <Grid
            container
            justifyContent="flex-end"
            className="table-search-result-utility-bar"
        >
            <Grid>
                <Button
                    color="secondary"
                    variant='outlined'
                    className='table-search-result-utility-bar-item'
                    onClick={() => {
                        let div = document.getElementById("table-copy");
                        if (!div) {
                            return;
                        }

                        div.className = "table-copy-show";

                        let selection = window.getSelection();
                        if (selection) {
                            let range = document.createRange();
                            range.selectNodeContents(divRef.current as Node);
                            selection.removeAllRanges();
                            selection.addRange(range);
                            document.execCommand("copy");
                            Utility.Alert.Success("Copied to clipboard");
                        }

                        div.className = "table-copy-hide";
                    }}
                >Copy contact details to clipboard</Button>
            </Grid>
            <Grid
                className='table-search-result-utility-bar-item table-search-result-utility-bar-result-found'
            >
                {SurveyorData.searchResultTotalRecord + " results found"}
            </Grid>
        </Grid>
        <Datatable<SurveyorModel>
            useCheckBox={true}
            useDefaultSearchBar={false}
            pagingType={PagingTypeEnum.None}
            data={SurveyorData.searchResults}
            useItemPerPage={false}
            columns={columns}
            isDataLoaded={true}
            className={getTableClassName()}
            useHeaderSort={true}
            onOrderDirectionChange={(column: DatatableColumnModel<SurveyorModel>, orderDirection: OrderDirection) => {
                dispatch(setOrderBy(column.Field));
                dispatch(setOrderDirection(orderDirection));
                dispatch(setTriggerSurveyorSearch((new Date()).getTime()));
            }}
            onRowSelectedChange={(tonnage: SurveyorModel, index: number, event: MouseEvent<HTMLElement>) => {
                var target = event.target as HTMLElement;
                if (target.tagName !== "BUTTON") {
                    dispatch(setSelectedSurveyorId(tonnage.id))
                    dispatch(setDatatableSelectedSurveyorIndex(index));
                }
            }}
            selectedRowIndex={SurveyorData.datatableSelectedIndex}
            stickyHeader={true}
            orderColumn={SurveyorData.query.orderBy}
            orderDirection={SurveyorData.query.orderDirection}
            onTableCheckboxSelectedChange={(selectedRows: SurveyorModel[]) => {
                dispatch(setCheckedSurveyors(selectedRows));
            }}
        ></Datatable>
        <Popover
            id="mouse-over-popover"
            sx={{
                pointerEvents: 'none',
            }}
            open={anchorEl != null}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            disableRestoreFocus
        >
            <Typography sx={{ p: 1 }}>{overviewFullTc}</Typography>
        </Popover>
        <div id="table-copy"
            className='table-copy-hide'
            contentEditable="true"
            ref={divRef}
        >
            <Datatable<SurveyorModel>
                useDefaultSearchBar={false}
                pagingType={PagingTypeEnum.None}
                data={SurveyorData.checkedSurveyors}
                useItemPerPage={false}
                columns={columnsForCopy}
                isDataLoaded={true}
                className={getTableClassName()}
            ></Datatable>
        </div>
    </div>
};

export default SurveyorTable;